import { Space } from 'antd';
import {
    CopyOutlined,
    ContainerOutlined,
    FolderOpenOutlined,
    DeleteOutlined,
    BarcodeOutlined,
    StarOutlined,
    StarFilled,
} from '@ant-design/icons';
import { MouseEventHandler, ReactElement, useCallback } from 'react';
import { StyledPopoverButton } from 'components/new-ui/composed/popover-buttons-group/styles';
import { DeclarationStatuses } from '../../../store/declarations/enums/common/declaration-status';
import { Declaration } from '../../../store/declarations/declaration';

export const CoreTemplateButton = ({
    record,
    onClick,
}: {
    record: any;
    onClick?: (record: any) => void;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.(record);
        },
        [onClick, record]
    );

    return (
        <StyledPopoverButton onClick={handleClick} data-testid="declaration-core-template-button-row">
            <Space size={8}>
                {!record?.isCoreTemplate ? <StarOutlined /> : <StarFilled />}
                {!record?.isCoreTemplate ? 'Use as Template' : 'Remove as Template'}
            </Space>
        </StyledPopoverButton>
    );
};

export const BarcodeButton = ({
    disabled,
    onClick,
    record,
}: {
    disabled: boolean;
    onClick?: (record: Declaration | undefined) => void;
    record?: Declaration | undefined;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.(record);
        },
        [onClick, record]
    );
    return (
        <StyledPopoverButton onClick={handleClick} data-testid="declaration-barcode-button-row" disabled={disabled}>
            <Space size={8}>
                <BarcodeOutlined />
                Barcode
            </Space>
        </StyledPopoverButton>
    );
};

export const DeleteButton = ({
    record,
    disabled,
    onClick,
}: {
    record: any;
    disabled: boolean;
    onClick?: (recordId: string) => void;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.(record.id);
        },
        [onClick, record.id]
    );

    const disabledBtn = disabled || record.status !== DeclarationStatuses.DRAFT;
    return (
        <StyledPopoverButton onClick={handleClick} data-testid="declaration-delete-button-row" disabled={disabledBtn}>
            <Space size={8}>
                <DeleteOutlined />
                Delete
            </Space>
        </StyledPopoverButton>
    );
};

export const DocumentsButton = ({
    record,
    onClick,
}: {
    record: any;
    onClick?: (recordId: string) => void;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.(record.id);
        },
        [onClick, record.id]
    );

    const disabled =
        record.status === DeclarationStatuses.DRAFT ||
        !(record?.irelandImportDeclaration || record?.irelandH7ImportDeclaration || record?.ieExportDeclaration);
    return (
        <StyledPopoverButton onClick={handleClick} data-testid="declaration-documents-button-row" disabled={disabled}>
            <Space size={8}>
                <FolderOpenOutlined />
                Documents
            </Space>
        </StyledPopoverButton>
    );
};

export const DuplicateButton = ({
    record,
    onClick,
}: {
    record: Declaration;
    onClick?: (record: Declaration) => void;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.(record);
        },
        [onClick, record]
    );
    return (
        <StyledPopoverButton data-testid="declaration-duplicate-button-row" onClick={handleClick}>
            <Space size={8}>
                <CopyOutlined />
                Duplicate
            </Space>
        </StyledPopoverButton>
    );
};

export const ArchiveButton = ({
    record,
    onClick,
    disabled,
}: {
    record: any;
    disabled: boolean;
    onClick?: (recordIds: string[]) => void;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.([record.id]);
        },
        [onClick, record.id]
    );
    return (
        <StyledPopoverButton disabled={disabled} onClick={handleClick} data-testid="declaration-archive-button-row">
            <Space size={8}>
                <ContainerOutlined />
                Archive
            </Space>
        </StyledPopoverButton>
    );
};

export const UnarchiveButton = ({
    record,
    onClick,
    disabled,
}: {
    record: any;
    onClick?: (recordIds: string[]) => void;
    disabled: boolean;
}): ReactElement => {
    const handleClick = useCallback<MouseEventHandler<HTMLElement>>(
        (e) => {
            e.stopPropagation();
            onClick?.([record.id]);
        },
        [onClick, record.id]
    );
    return (
        <StyledPopoverButton disabled={disabled} onClick={handleClick} data-testid="declaration-unarchive-button-row">
            <Space size={8}>
                <ContainerOutlined /> Unarchive
            </Space>
        </StyledPopoverButton>
    );
};
